import { getAccessToken, refreshAccessToken } from "./auth";
import { API_BASE_URL, ApiError, unwrap } from "./http";

export { API_BASE_URL, ApiError };

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** Objects are JSON-encoded; FormData is passed through for photo upload. */
  body?: unknown;
  /** Skip the bearer token (login, refresh). */
  anonymous?: boolean;
}

function buildInit(options: ApiFetchOptions, token: string | null): RequestInit {
  // `body` and `anonymous` are ours, not RequestInit's — peel them off.
  const { body: _body, anonymous: _anonymous, ...rest } = options;

  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (token !== null) headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = { ...rest, headers };

  if (options.body !== undefined) {
    if (options.body instanceof FormData) {
      // Leave Content-Type unset so the browser adds the multipart boundary.
      init.body = options.body;
    } else if (typeof options.body === "string") {
      init.body = options.body;
      headers.set("Content-Type", "application/json");
    } else {
      init.body = JSON.stringify(options.body);
      headers.set("Content-Type", "application/json");
    }
  }

  return init;
}

/**
 * Authenticated fetch. On a 401 it refreshes once — through the shared
 * single-flight promise — and retries. A second 401 is surfaced rather than
 * looping.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const token = options.anonymous === true ? null : getAccessToken();
  const response = await fetch(
    `${API_BASE_URL}${path}`,
    buildInit(options, token),
  );

  if (response.status !== 401 || options.anonymous === true) {
    return unwrap<T>(response);
  }

  const refreshed = await refreshAccessToken();
  const retry = await fetch(
    `${API_BASE_URL}${path}`,
    buildInit(options, refreshed),
  );
  return unwrap<T>(retry);
}

/**
 * Authenticated binary fetch — for the `/media/*` endpoint, whose bearer token
 * can't ride along on a plain `<img src>`. Refreshes once on a 401 like
 * apiFetch does, then hands back the raw blob for an object URL.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const run = async (token: string | null): Promise<Response> =>
    fetch(`${API_BASE_URL}${path}`, {
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });

  let response = await run(getAccessToken());
  if (response.status === 401) {
    response = await run(await refreshAccessToken());
  }
  if (!response.ok) {
    throw new ApiError(response.status, "MEDIA_UNAVAILABLE", "media unavailable", null);
  }
  return response.blob();
}
